import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CircularProgress } from '@material-ui/core';
import { HomeDevice, KeyValuePair } from '@moderepo/mode-internal-apis';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { BaseListCompField, capitalizeAll, compareNumbers, compareObjects, compareStrings, ITEMS_PER_PAGE_10, ITEMS_PER_PAGE_LIST,
    GenericMessageBox, PaginationBar, PAGING_MIN_PAGES, TimeUnit, useSearchParams } from '@moderepo/mode-app-components';
import { userAppDataContext, selectAllHomeKVStores, fetchAllHomeKVStores, selectAllDeviceKVStores, fetchAllDeviceKVStores, selectHomeDevices,
    fetchHomeDevices } from '@moderepo/mode-data-state-user-app';
import { useNavigate } from 'react-router-dom';
import { modeUIContext, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import produce from 'immer';
import { SortOrder } from '@moderepo/mode-apis';
import { useRitzTranslation } from '../../utils/customHooks';
import * as SCUtils from './SCUtils';
import { SCSensorModulesTable, SCSensorModulesTableProps } from '../../components/sensor-cloud/SCSensorModulesTable';
import { SCRouteHelper } from './SCRouteHelper';
import { SCSensorModulePreviewCompOptions, SCSlideOutPanelCompType } from './SCProjectConfig';


export interface SCSensorModulesTableHiddenFields {
    readonly id?: boolean | undefined;
    readonly name?: boolean | undefined;
    readonly modelId?: boolean | undefined;
    readonly gatewayId?: boolean | undefined;
    readonly gatewayName?: boolean | undefined;
    readonly sensing?: boolean | undefined;
    readonly sensors?: boolean | undefined;
    readonly interval?: boolean | undefined;
}


interface SensorModulesSearchParams {
    readonly sortField: keyof SCUtils.SensorModule;
    readonly sortOrder: SortOrder;
}


interface SCSensorModulesListingContainerProps {
    readonly projectId: number;

    // The container, 'home' or 'device', which the sensor modules belonged to and it's ID, 'homeId' or 'deviceId'.
    readonly smContainer: SCUtils.SensorModuleContainer;
    readonly smContainerId: number;

    readonly userId?: number | undefined;           // These 3 props might be undefined depending on which page the user is on
    readonly homeId?: number | undefined;
    readonly deviceId?: number | undefined;
    readonly hiddenFields?: SCSensorModulesTableHiddenFields | undefined;
    readonly itemsPerPage?: number | undefined;
}


/**
 * This is the container which show all the sensors module belonged to a home or device.
 */
export const SCSensorModulesListingContainer: React.FC<SCSensorModulesListingContainerProps> = (props: SCSensorModulesListingContainerProps) => {
    const navigate = useNavigate();
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = selectProjectById(dataState, props.projectId);

    const devices = props.homeId ? selectHomeDevices(appDataState, props.homeId) : undefined;


    // Select the KV pairs from data state. If props.deviceId is provided, we will select Device KV pairs else we select Home KV pairs
    const keyValuePairs = props.smContainer === SCUtils.SensorModuleContainer.DEVICE
        ? selectAllDeviceKVStores(appDataState, props.smContainerId, {
            keyPrefix: SCUtils.SENSOR_MODULE_PREFIX,
        })
        : selectAllHomeKVStores(appDataState, props.smContainerId, {
            keyPrefix: SCUtils.SENSOR_MODULE_PREFIX,
        });



    const {
        searchParams, onItemsPerPageChange, onOtherFieldsChange, onPrevPage, onNextPage, onPage,
    } = useSearchParams<SensorModulesSearchParams>({
        searchText  : '',
        itemsPerPage: props.itemsPerPage || ITEMS_PER_PAGE_10,
        currentPage : 0,
        otherFields : {
            sortField: 'gatewayId',
            sortOrder: SortOrder.ASC,
        },
    });


    // For sensor cloud project, we will be using KeyValuePairs to store info about sensor modules. These are key/value pairs which
    // the key starts with SCUtils.SENSOR_MODULE_PREFIX.
    const sensorModules = keyValuePairs ? keyValuePairs.map((pair: KeyValuePair): SCUtils.SensorModule => {
        return SCUtils.fixSensorModuleData(pair.value as SCUtils.SensorModule);
    }) : undefined;


    /**
     * Because backend return ALL sensorModules, we have to sort from the front end
     */
    const sortedSensorModules = useMemo(() => {
        if (sensorModules) {

            // The mapping of fieldName => compareFunction. The compareObjects function doesn't know the value type of the field therefore
            // we need to tell it which function to use.
            const compareFunctionMapping = {
                interval : compareNumbers,
                id       : compareStrings,
                name     : compareStrings,
                gatewayId: compareStrings,
                sensing  : compareStrings,
                modelId  : compareStrings,
            };

            // Compare the 1 data using ASCENDING order and then multiply the result with -1 if the sort order is DESC
            return [...sensorModules].sort((module1: SCUtils.SensorModule, module2: SCUtils.SensorModule) => {

                const ascCompareResult = compareObjects(module1, module2, searchParams.otherFields.sortField, compareFunctionMapping);

                return ascCompareResult * (
                    searchParams.otherFields && searchParams.otherFields.sortOrder === SortOrder.DESC ? -1 : 1
                );
            });
        }
        return undefined;
    }, [searchParams.otherFields, sensorModules]);


    /**
     * Filter the sorted sensor modules based on the current page # and page size. These will be the modules that we will be showing
     */
    const filteredSensorModules = useMemo(() => {
        if (sortedSensorModules) {
            return sortedSensorModules.slice(
                searchParams.itemsPerPage * searchParams.currentPage, searchParams.itemsPerPage * (searchParams.currentPage + 1),
            );
        }

        return undefined;
    }, [searchParams.currentPage, searchParams.itemsPerPage, sortedSensorModules]);


    const { trans } = useRitzTranslation(project);
    const scRouteHelper = SCRouteHelper.getInstance(navigate);
    const [selectedSensorModuleId, setSelectedSensorModuleId] = useState<string>();


    /**
     * This useEffect is used for fetching sensor module list when the component is loaded
    */
    useEffect(() => {
        // if the list of key value pairs for this device is not loaded, load them
        if (!keyValuePairs) {
            if (props.smContainer === SCUtils.SensorModuleContainer.DEVICE) {
                appDataDispatch(fetchAllDeviceKVStores(props.smContainerId, SCUtils.SENSOR_MODULE_PREFIX)).catch(() => { /* ignore errors */ });
            } else {
                appDataDispatch(fetchAllHomeKVStores(props.smContainerId, SCUtils.SENSOR_MODULE_PREFIX)).catch(() => { /* ignore errors */ });
            }
        }
    }, [keyValuePairs, appDataDispatch, props.smContainer, props.smContainerId]);


    /**
     * This useEffect is used for fetching home's devices so that we can display gateway's name instead of gatewayId
     */
    useEffect(() => {
        if (props.smContainer === SCUtils.SensorModuleContainer.HOME && !devices) {
            appDataDispatch(fetchHomeDevices(props.smContainerId));
        }
    }, [appDataDispatch, dataDispatch, devices, props.smContainer, props.smContainerId, props.homeId]);


    /**
     * Localize the sensor modules' values for display and cache them
     */
    const localizedSensorModulesTableData = useMemo((): SCSensorModulesTableProps['listData'] => {
        if (filteredSensorModules) {
            return filteredSensorModules.map((sensorModule: SCUtils.SensorModule) => {
                const device = devices?.find((d: HomeDevice) => {
                    return d.id === Number(sensorModule.gatewayId);
                });

                return {
                    actualValue : sensorModule,
                    displayValue: {
                        id         : sensorModule.id,
                        name       : sensorModule.name,
                        modelId    : sensorModule.modelId,
                        gatewayId  : Number(sensorModule.gatewayId),
                        gatewayName: device?.name || capitalizeAll(trans('label.none')),
                        sensing    : sensorModule.sensing,
                        sensors    : sensorModule.sensors.join(', '),
                        interval   : sensorModule.interval,
                    },
                    selected: selectedSensorModuleId === sensorModule.id,
                };
            });
        }

        return undefined;
    }, [devices, filteredSensorModules, selectedSensorModuleId, trans]);


    /**
     * On column clicked, change the sort order/field
     */
    const onSortByField = useCallback((field: BaseListCompField) => {
        if (searchParams.otherFields) {
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: any) => {

                if (draft.sortField === field.dataItemProp) {
                    // this column is currently being sorted, change the toggle the sort order instead
                    if (draft.sortOrder === SortOrder.ASC) {
                        draft.sortOrder = SortOrder.DESC;
                    } else {
                        draft.sortOrder = SortOrder.ASC;
                    }
                } else {
                    // This column is not the current column being sorted, change it to this column and
                    draft.sortField = field.dataItemProp;
                }
            }));
        }
    }, [searchParams, onOtherFieldsChange]);


    const sensorModulesTableFieldsSetting = useMemo((): SCSensorModulesTableProps['fieldsSettings'] => {
        return {
            fields: {
                preview: {
                    label: '',      // The column doesn't need a label so we can leave it blank
                },
                id: {
                    dataItemProp: 'id',
                    label       : capitalizeAll(trans('label.id')),
                    sortable    : true,
                    hidden      : props.hiddenFields?.id,
                },
                name: {
                    dataItemProp: 'name',
                    label       : capitalizeAll(trans('label.name')),
                    sortable    : true,
                    hidden      : props.hiddenFields?.name,
                },
                modelId: {
                    dataItemProp: 'modelId',
                    label       : capitalizeAll(trans('label.model_id')),
                    sortable    : true,
                    hidden      : props.hiddenFields?.modelId,
                },
                gatewayId: {
                    dataItemProp: 'gatewayId',
                    label       : capitalizeAll(trans('label.gateway_id')),
                    sortable    : true,
                    hidden      : props.hiddenFields?.gatewayId,
                },
                gatewayName: {
                    label : capitalizeAll(trans('label.gateway_name')),
                    hidden: props.hiddenFields?.gatewayName,
                },
                sensing: {
                    dataItemProp: 'sensing',
                    label       : capitalizeAll(trans('label.sensing')),
                    sortable    : true,
                    hidden      : props.hiddenFields?.sensing,
                },
                interval: {
                    dataItemProp: 'interval',
                    label       : `${capitalizeAll(trans('label.interval'))} (${trans(TimeUnit.SECOND_ABBREV, { count: 2 })})`,
                    sortable    : false,
                    hidden      : props.hiddenFields?.interval,
                },
                sensors: {
                    dataItemProp: 'sensors',
                    label       : capitalizeAll(trans('label.sensor', { count: 2 })),
                    sortable    : false,
                    hidden      : props.hiddenFields?.sensors,
                },
            },
            onSortByField,
            currentSortedField: searchParams.otherFields ? searchParams.otherFields.sortField : undefined,
            currentSortedOrder: searchParams.otherFields ? searchParams.otherFields.sortOrder : undefined,
        };
    }, [onSortByField, props.hiddenFields, searchParams.otherFields, trans]);


    // Create PaginationBar and cache it
    const paginationComp = useMemo(() => {
        return (
            <PaginationBar {...{
                itemsPerPageList: ITEMS_PER_PAGE_LIST,
                itemsPerPage    : searchParams.itemsPerPage,
                pagingMinPages  : PAGING_MIN_PAGES,
                currentPage     : searchParams.currentPage,
                totalItems      : sortedSensorModules ? sortedSensorModules.length : undefined,
                hasMoreItems    : sortedSensorModules !== undefined
                                    && sortedSensorModules.length > ((searchParams.currentPage + 1) * searchParams.itemsPerPage),
                texts: {
                    itemsPerPage    : capitalizeAll(trans('label.paging.items_per_page', { item_name: trans('label.item', { count: 2 }) })),
                    pageNTemplate   : capitalizeAll(trans('label.page_n')),
                    pageNofMTemplate: capitalizeAll(trans('label.page_n_of_m')),
                },
                onItemsPerPageChange,
                onNextPage,
                onPrevPage,
                onPage,
            }}
            />
        );
    }, [searchParams.itemsPerPage, searchParams.currentPage, sortedSensorModules, trans, onItemsPerPageChange, onNextPage, onPrevPage, onPage]);



    /**
     * On sensor module selected, take the agent to the sensor module details page
     */
    const onSensorModuleSelected = useCallback((sensorModule: SCUtils.SensorModule) => {
        if (props.smContainer === SCUtils.SensorModuleContainer.DEVICE) {
            scRouteHelper.gotoDeviceSensorModuleDetailsPage({
                projectId: props.projectId,
                moduleId : sensorModule.id,
                deviceId : Number(sensorModule.gatewayId),
                userId   : props.userId,
                homeId   : props.homeId,
            });
        } else {
            scRouteHelper.gotoHomeSensorModuleDetailsPage({
                projectId: props.projectId,
                moduleId : sensorModule.id,
                userId   : props.userId,
                homeId   : props.smContainerId,
            });

        }
    }, [props.smContainer, props.projectId, props.userId, props.homeId, props.smContainerId, scRouteHelper]);


    /**
     * On preview button clicked, show slide out panel for the sensor module
     */
    const onPreviewSensorModuleClicked = useCallback((sensorModule: SCUtils.SensorModule) => {
        setSelectedSensorModuleId(sensorModule.id);
        const options: SCSensorModulePreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : SCSlideOutPanelCompType.SENSOR_MODULE_PREVIEW,
            compProps: {
                data: {
                    projectId    : props.projectId,
                    moduleId     : sensorModule.id,
                    smContainer  : props.smContainer,
                    smContainerId: props.smContainerId,
                },
                context: {
                    userId  : props.userId,
                    homeId  : props.smContainer === 'home' ? props.smContainerId : undefined,
                    deviceId: Number(sensorModule.gatewayId),
                },
            },
        };
        uiDispatch(showSlideOutPanel(options));

    }, [props.smContainerId, props.smContainer, props.projectId, props.userId, uiDispatch]);


    return (
        <>
            {localizedSensorModulesTableData && (
                <SCSensorModulesTable
                    title={capitalizeAll(trans('label.sensor_module', { count: 2 }))}
                    icon="settings_input_antenna"
                    fieldsSettings={sensorModulesTableFieldsSetting}
                    listData={localizedSensorModulesTableData}
                    onListItemSelected={onSensorModuleSelected}
                    onPreviewItemClicked={onPreviewSensorModuleClicked}
                    paginationComp={paginationComp}
                    noDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('terms.sensor_module', { count: 2 })),
                            })}
                        />
                    )}
                    emptyDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('label.sensor_module', { count: 2 })),
                            })}
                        />
                    )}
                />
            )}

            {!sensorModules && (
                <CircularProgress />
            )}
        </>
    );
};
